import { api } from "../../../../api";
import {
  AggregatedPackingPropsResponse,
  CargoReleaseResponse,
  ContainerWarehousingCreate,
  CreateCargoRelease,
  CreatePickingServiceForm,
  DepositorChangeResponse,
  FCRItemsProps,
  PackingProps,
  PackingPropsResponse,
  PickingServiceResponse,
  ShiftingCreate,
  ShiftingDetailsResponse,
  UnloadingBulk,
  UnloadingContainer,
  UnloadingItem,
  UnloadingItemResponse,
  UnloadingNotes,
  WarehousingResponse,
} from "./types";

export async function getUnloadingApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<UnloadingItemResponse> {
  const { data } = await api.get<UnloadingItemResponse>(
    `/api/shipping-cases/${id}/bl-containers/unloadings`,
    { params },
  );
  return data;
}

export async function getBulkUnloadingApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<UnloadingItemResponse> {
  const { data } = await api.get<UnloadingItemResponse>(
    `/api/shipping-cases/${id}/bill-of-lading/unloadings`,
    { params },
  );
  return data;
}

export async function updateUnloadingNotesApi(
  id: number,
  params: Partial<UnloadingNotes>,
): Promise<void> {
  await api.patch<UnloadingItemResponse>(`/api/shipping-cases/${id}/notes`, {
    ...params,
  });
}

export async function getWarehousingApi(
  id: number,
): Promise<WarehousingResponse> {
  const { data } = await api.get<WarehousingResponse>(
    `/api/shipping-cases/${id}/warehousing`,
  );
  return data;
}

export async function getShiftingApi(
  id: number,
): Promise<{ items: ShiftingDetailsResponse[] }> {
  const { data } = await api.get<{ items: ShiftingDetailsResponse[] }>(
    `/api/shipping-cases/${id}/warehouse-shiftings`,
  );
  return data;
}

export async function updateShiftingApi(
  id: number,
  params: Partial<ShiftingCreate>,
): Promise<ShiftingDetailsResponse> {
  const { data } = await api.put<ShiftingDetailsResponse>(
    `/api/shipping-cases/${id}/warehouse-shiftings`,
    {
      ...params,
    },
  );
  return data;
}

export async function removeShiftingApi(id: number): Promise<void> {
  await api.delete<void>(`/api/warehouse-shiftings/${id}`);
}

export async function cancelShiftingApi(
  id: number,
): Promise<ShiftingDetailsResponse> {
  const { data } = await api.post<ShiftingDetailsResponse>(
    `/api/warehouse-shiftings/${id}/cancel`,
  );
  return data;
}

export async function getDepositaryApi(
  id: number,
): Promise<{ items: DepositorChangeResponse[] }> {
  const { data } = await api.get<{ items: DepositorChangeResponse[] }>(
    `/api/shipping-cases/${id}/depositary-changes`,
  );
  return data;
}

export async function updateDepositaryApi(
  id: number,
  params: Partial<ShiftingCreate>,
): Promise<DepositorChangeResponse> {
  const { data } = await api.put<DepositorChangeResponse>(
    `/api/shipping-cases/${id}/depositary-changes`,
    {
      ...params,
    },
  );
  return data;
}

export async function removeDepositaryApi(id: number): Promise<void> {
  await api.delete<void>(`/api/depositary-changes/${id}`);
}

export async function getPickingOrdersApi(
  id: number,
): Promise<{ items: PickingServiceResponse[] }> {
  const { data } = await api.get<{ items: PickingServiceResponse[] }>(
    `/api/shipping-cases/${id}/picking-orders`,
  );
  return data;
}

export async function updatePickingOrdersApi(
  id: number,
  params: Partial<CreatePickingServiceForm>,
): Promise<PickingServiceResponse> {
  const { data } = await api.put<PickingServiceResponse>(
    `/api/shipping-cases/${id}/picking-orders`,
    {
      ...params,
    },
  );
  return data;
}

export async function removePickingOrdersApi(id: number): Promise<void> {
  await api.delete<void>(`/api/picking-orders/${id}`);
}

export async function getFcrApi(
  id: number,
): Promise<{ items: CargoReleaseResponse[] }> {
  const { data } = await api.get<{ items: CargoReleaseResponse[] }>(
    `/api/shipping-cases/${id}/fcrs`,
  );
  return data;
}

export async function updateFcrApi(
  id: number,
  params: Partial<CreateCargoRelease>,
): Promise<CargoReleaseResponse> {
  const { data } = await api.put<CargoReleaseResponse>(
    `/api/shipping-cases/${id}/fcrs`,
    {
      ...params,
    },
  );
  return data;
}

export async function removeFcrApi(id: number): Promise<void> {
  await api.delete<void>(`/api/fcrs/${id}`);
}

export async function getFcrPackingListApi(
  id: number,
  params: any,
): Promise<{
  items: FCRItemsProps[];
  size: number;
  total: number;
  page: number;
}> {
  const { data } = await api.get<{
    items: FCRItemsProps[];
    size: number;
    total: number;
    page: number;
  }>(`/api/fcrs/${id}/packing-lists`, { params });
  return data;
}

export async function getPackingListByShiftingApi(
  sid: number,
  id: number,
  params: any,
): Promise<{
  items: PackingProps[];
  size: number;
  total: number;
  page: number;
}> {
  const { data } = await api.get<{
    items: PackingProps[];
    size: number;
    total: number;
    page: number;
  }>(`/api/shipping-cases/${sid}/packing-lists/short?whs_id=${id}`, { params });
  return data;
}

export async function updateFcrPackingListApi(
  id: number,
  params: {
    packing_list_ids: number[];
    release_date: string;
  },
): Promise<FCRItemsProps> {
  const { data } = await api.post<FCRItemsProps>(
    `/api/fcrs/${id}/packing-lists/release`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateWarehousingApi(
  id: number,
  params: Partial<ContainerWarehousingCreate>,
): Promise<WarehousingResponse> {
  const { data } = await api.put<WarehousingResponse>(
    `/api/shipping-cases/${id}/warehousing`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateBulkUnloadingApi(
  id: number,
  params: Partial<UnloadingBulk>,
): Promise<UnloadingBulk> {
  const { data } = await api.put<UnloadingBulk>(
    `/api/bill-of-lading/${id}/unloadings`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateContainerUnloadingApi(
  id: number,
  params: Partial<UnloadingItem>,
): Promise<UnloadingContainer> {
  const { data } = await api.put<UnloadingContainer>(
    `/api/bill-of-lading/${id}/unloadings`,
    {
      ...params,
    },
  );
  return data;
}

export async function getPackingShortApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<PackingPropsResponse> {
  const { data } = await api.get<PackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/short`,
    { params },
  );
  return data;
}

export async function getAggregatedPackingShortApi(
  id: number,
  params: Record<string, any>,
): Promise<AggregatedPackingPropsResponse> {
  const { data } = await api.get<AggregatedPackingPropsResponse>(
    `/api/shipping-cases/${id}/packing-lists/aggregated`,
    { params },
  );
  return data;
}

export async function getShiftingDeliveryReadinessApi(
  id: number,
): Promise<{ ready?: boolean; missed_columns?: string[]; empty?: boolean }> {
  const { data } = await api.get<{
    ready?: boolean;
    missed_columns?: string[];
    empty?: boolean;
  }>(`/api/shipping-cases/${id}/packing-list/check-columns-emptiness`);
  return data;
}
